
import {computed, defineComponent, PropType, reactive, ref} from 'vue';
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Checkbox from "primevue/checkbox";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import {DocumentLocator, DocumentTableColumn} from "@dex/squeeze-client-ts";
import Tooltip from "primevue/tooltip";
import {DocumentFilterObject} from "@/apps/squeeze/interfaces/DocumentSearch";
import {FilterMatchMode} from "primevue/api";
import {showDropdownOverlay} from "@/util/StylesHelper";

export default defineComponent({
	name: 'DocumentClassTableColumns',
	methods: {showDropdownOverlay},
	components: {
		DataTable,
		Column,
		Checkbox,
		InputText,
		Dropdown,
	},
	props: {
		/** Show loading in the table? */
		loading: {
			type: Boolean,
			default: false,
		},
		/** All rows */
		rows: {
			type: Array as PropType<DocumentTableColumn[]>,
			default: () => [],
		},
		/** All available Locators */
		locators: {
			type: Array as PropType<DocumentLocator[]>,
			default: () => [],
		},
		tableId: {
			type: Number,
			default: 0,
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: ["openEntryDialog", "deleteEntry", "onChangeCheckbox", "onChangeSortOrder"],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();

		/** Data of selected row */
		const rowData = reactive<DocumentTableColumn>({});

		/** Is the Search activated? */
		const searchActive = ref<boolean>(false);

		/** Filters of list (Currently not activated) */
		const filters = ref<DocumentFilterObject>({});

		/** Is Filtering active? */
		const isFilterActive = ref<boolean>(false);

		/** List of Data-Types */
		const dataTypes = computed(() => {
			return [
				{
					id: 'Text',
					text: t('Squeeze.General.Text'),
				},
				{
					id: 'Date',
					text: t('Squeeze.General.Date'),
				},
				{
					id: 'Amount',
					text: t('Squeeze.General.Amount'),
				},
			];
		});

		/**
		 * Get the Translation of data type
		 * @param {string} type
		 * @return {string} Current Translation of data type
		 */
		const getTranslationFromDataType = (type: string): string => {
			switch (type) {
			case 'Text':
				return t('Squeeze.General.Text');
			case 'Date':
				return t('Squeeze.General.Date');
			case 'Amount':
				return t('Squeeze.General.Amount');
			default:
				return type;
			}
		}

		/**
		 * Opens the Delete Dialog
		 * @param {DocumentTableColumn} data Data from the Table that should be deleted
		 */
		const openDeleteDialog = (data: DocumentTableColumn) => {
			Object.assign(rowData, data);
			emit("deleteEntry", rowData);
		}

		/**
		 * Opens the Entry-Edit-Dialog
		 * @param {DocumentTableColumn} data Data to edit
		 */
		const openEntryDialog = (data: DocumentTableColumn) => {
			if (!data.lookup) {
				data.lookup = {
					active: false,
					allowCustomValues: true,
					tableId: 0,
					resultKeyColumnId: 0,
					searchColumnIds: [],
					resultValueColumnIds: [],
					minInputLength: 1,
				};
			}

			// reset data
			if (!data.id) {
				data.id = undefined;
				data.name = undefined;
				data.description = undefined;
				data.dataType = undefined;
				data.headerLocatorId = undefined;
				data.valueLocatorId = undefined;
				data.mandatory = undefined;
				data.readonly = undefined;
				data.hidden = undefined;
				data.forceValidation = undefined;
				data.sortOrder = undefined;
				data.tableId = undefined;
				data.externalName = undefined;
			}

			data.tableId = props.tableId;
			emit("openEntryDialog", data);
		}

		/**
		 * Gets the Description of a locator
		 * @param {Number} locatorId Id of the Locator to get the Description from
		 * @return {String} Description of the Locator
		 */
		const getDescriptionFromLocator = (locatorId: number) => {
			// No locator at all
			if (locatorId === 0) {
				return ''
			}

			if (!props.locators) {
				return String(locatorId);
			}

			const locator = props.locators.find(locator => locator.id === locatorId);
			if (!locator) {
				return String(locatorId);
			}

			return locator.description;
		}

		/** Emits the "deleteEntry"-Event */
		const deleteEntry = () => {
			emit("deleteEntry", rowData);
		}

		/**
		 * Triggered when a checkbox is clicked
		 * @param {DocumentTableColumn} data Data to edit
		 * @param {string} fieldName Name of the current field
		 */
		const onChangeCheckbox = (data: DocumentTableColumn, fieldName: string) => {
			emit("onChangeCheckbox", data, fieldName)
		}

		/**
		 * Triggered when the rows are reordered
		 * @param event
		 */
		const onRowReorder = (event: any) => {
			const orderedList: number[] = [];
			event.value.forEach((column: DocumentTableColumn) => {
				orderedList.push(column.id!);
			});
			emit("onChangeSortOrder", orderedList, props.tableId, isFilterActive.value);
		}

		/**
		 * Triggered when a filter has been entered
		 * @param event
		 */
		const onFilter = (event: any) => {
			isFilterActive.value = event.filteredValue.length < props.rows.length;
		}

		/** Init filters */
		const initFilters = () => {
			filters.value = {
				"id": {value: null, matchMode: FilterMatchMode.EQUALS},
				"name": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"description": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"dataType": {value: null, matchMode: FilterMatchMode.EQUALS},
				"headerLocatorId": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"valueLocatorId": {value: null, matchMode: FilterMatchMode.EQUALS},
				"externalName": {value: null, matchMode: FilterMatchMode.CONTAINS},
			};
		}

		/** Clear filters */
		const clearFilters = () => {
			initFilters();
		}

		initFilters();

		return {
			t,
			toast,
			rowData,
			searchActive,
			filters,
			isFilterActive,
			dataTypes,
			getTranslationFromDataType,
			openDeleteDialog,
			openEntryDialog,
			getDescriptionFromLocator,
			deleteEntry,
			onChangeCheckbox,
			onRowReorder,
			onFilter,
			initFilters,
			clearFilters,
		};
	},
});

